import { Card } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import type { AdminApplication } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { CustomDataGrid, type DataGridColumn } from '@/components/shell/CustomDataGrid';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { applicationStatusTone } from './domain';

interface ApplicationsListViewProps {
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export function ApplicationsListView({ page, pageSize, onPageChange }: ApplicationsListViewProps) {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.listAdminApplications(page, pageSize),
    `${page}:${pageSize}`,
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="list" rows={8} />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.applications.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <EmptyState title={translate('pay.applications.list.emptyTitle')} body={translate('pay.applications.list.emptyBody')} />
      </Card>
    );
  }

  const columns: Array<DataGridColumn<AdminApplication>> = [
    {
      key: 'code',
      label: translate('pay.applications.list.columnCode'),
      mono: 'code',
      render: (item) => item.code,
    },
    {
      key: 'name',
      label: translate('pay.applications.list.columnName'),
      render: (item) => item.name,
    },
    {
      key: 'status',
      mobileVisible: true,
      label: translate('pay.applications.list.columnStatus'),
      render: (item) => (
        <StatusChip label={translate(`pay.applications.status.${item.status}`)} tone={applicationStatusTone(item.status)} />
      ),
    },
    {
      key: 'notifyUrl',
      label: translate('pay.applications.list.columnNotifyUrl'),
      mono: 'code',
      render: (item) => item.notifyUrl ?? translate('pay.applications.list.notifyUrlEmpty'),
    },
    {
      key: 'createdAt',
      label: translate('pay.applications.list.columnCreatedAt'),
      render: (item) => new Date(item.createdAt).toLocaleString('es-ES'),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={data.items}
      getRowKey={(item) => item.code}
      onRowClick={(item) => navigate(`/applications/${encodeURIComponent(item.code)}`)}
      rowAriaLabel={translate('pay.applications.list.viewDetail')}
      pagination={{
        page: data.page.page,
        pageSize: data.page.pageSize,
        totalItems: data.page.totalItems,
        rowsPerPageLabel: translate('pay.applications.list.rowsPerPage'),
        onPageChange: (newPage) => onPageChange(newPage, pageSize),
        onPageSizeChange: (newPageSize) => onPageChange(1, newPageSize),
      }}
    />
  );
}
